import { useEffect, useRef } from "react";

/**
 * Keyboard shortcuts (docs/DESIGN-SYSTEM.md#keyboard). The same everywhere:
 * "?" lists them, "g" then a letter goes to a page, "/" and Cmd/Ctrl+K
 * search, Escape closes the top panel or dialog. Pages may add a few of
 * their own with usePageShortcuts; they are listed under "This page".
 * Nothing fires while typing in a field.
 */
export type Shortcut = { keys: string[]; label: string };
export type GoShortcut = { key: string; page: string; label: string };

/** "g" then … : the pages everyone reaches most (only shown when the person can open them). */
export const GO_SHORTCUTS: GoShortcut[] = [
  { key: "t", page: "today", label: "Today" },
  { key: "c", page: "counts", label: "Count inventory" },
  { key: "i", page: "inventory", label: "Products & stock" },
  { key: "o", page: "purchasing", label: "Orders & deliveries" },
  { key: "v", page: "invoices", label: "Invoices" },
  { key: "m", page: "recipes", label: "Recipes & batches" },
  { key: "r", page: "reports", label: "Reports" },
  { key: "h", page: "history", label: "History" },
  { key: "a", page: "alerts", label: "Alerts" },
  { key: "s", page: "settings", label: "Workspace settings" },
];

export const GLOBAL_SHORTCUTS: Shortcut[] = [
  { keys: ["?"], label: "Show keyboard shortcuts" },
  { keys: ["Mod", "K"], label: "Search everything" },
  { keys: ["/"], label: "Search everything" },
  { keys: ["Esc"], label: "Close the panel or dialog on top" },
];

export const isMac = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
/** Display name of a key ("Mod" is ⌘ on a Mac, Ctrl elsewhere). */
export const keyName = (k: string) => (k === "Mod" ? (isMac() ? "⌘" : "Ctrl") : k);

/** True when a keystroke belongs to a field (typing), not to the app. */
export function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "textarea" || tag === "select") return true;
  if (tag === "input") {
    const type = ((el as HTMLInputElement).type || "text").toLowerCase();
    return !["button", "checkbox", "radio", "submit", "reset", "range", "color", "file"].includes(type);
  }
  return el.isContentEditable || el.closest?.("[contenteditable='true']") != null;
}
/** A dialog or panel is open (page navigation shortcuts wait until it closes). */
export const layerOpen = () => typeof document !== "undefined" && !!document.querySelector('[aria-modal="true"]');

/* ---- page shortcuts ---------------------------------------------- */

export type PageShortcut = { key: string; label: string; run: () => void };
const pageShortcuts = new Map<symbol, PageShortcut[]>();
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());

/** A page's own single-key shortcuts (e.g. "n" for a new invoice), active while it is mounted. */
export function usePageShortcuts(list: PageShortcut[]) {
  const key = useRef(Symbol("page-shortcuts"));
  const latest = useRef(list);
  latest.current = list;
  const signature = list.map((s) => `${s.key}:${s.label}`).join("|");
  useEffect(() => {
    const k = key.current;
    pageShortcuts.set(
      k,
      latest.current.map((s) => ({ ...s, run: () => latest.current.find((x) => x.key === s.key)?.run() })),
    );
    changed();
    return () => {
      pageShortcuts.delete(k);
      changed();
    };
  }, [signature]);
}
export const currentPageShortcuts = (): PageShortcut[] => [...pageShortcuts.values()].flat();
export function onPageShortcutsChange(fn: () => void) {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}
